import React, { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function PostFitModal({ open, onClose, onPosted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [caption, setCaption] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!open) return;
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      // reset on close
      setFile(null);
      setPreview("");
      setCaption("");
      setProductId("");
    }
  }, [open]);

  if (!open) return null;

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Pick a photo first");
      return;
    }
    setBusy(true);
    try {
      // 1) upload image
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/community/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // 2) create post
      const payload = {
        image_url: up.data.url,
        caption: caption.trim(),
        product_id: productId || null,
      };
      await api.post("/community/posts", payload);
      toast.success("FIT SUBMITTED", { description: "Pending admin approval — you'll see it on the wall once it's live." });
      onPosted?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="post-fit-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-[#27272A] w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#27272A] sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <p className="label-mono text-[#CFF048]">// POST YOUR FIT</p>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">Drop it on the wall</h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#71717A] hover:text-white transition-colors"
            data-testid="close-post-modal"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {/* Image upload */}
          <div>
            <p className="label-mono text-[#71717A] mb-2">// PHOTO</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onPick}
              className="hidden"
              data-testid="post-photo-input"
            />
            {preview ? (
              <div className="relative">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full max-h-[420px] object-contain bg-black border border-[#27272A]"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="absolute top-3 right-3 bg-black/80 border border-[#CFF048] text-[#CFF048] px-3 py-2 label-mono hover:bg-[#CFF048] hover:text-black transition-colors"
                  data-testid="change-photo-btn"
                >
                  CHANGE
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-[#27272A] hover:border-[#CFF048] py-16 flex flex-col items-center gap-3 text-[#71717A] hover:text-[#CFF048] transition-colors"
                data-testid="pick-photo-btn"
              >
                <Upload size={28} />
                <span className="label-mono">UPLOAD A PHOTO (MAX 8MB)</span>
                <span className="text-xs text-[#52525B]">JPG, PNG, WEBP, HEIC</span>
              </button>
            )}
          </div>

          {/* Product */}
          <div>
            <p className="label-mono text-[#71717A] mb-2">// WHICH TEE? (OPTIONAL)</p>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="cult-input"
              data-testid="post-product-select"
            >
              <option value="">— Pick a Cult Lyf product —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Caption */}
          <div>
            <p className="label-mono text-[#71717A] mb-2">// CAPTION (OPTIONAL · 240 CHARS)</p>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 240))}
              placeholder="Drop a line about your fit..."
              rows={3}
              className="cult-input"
              data-testid="post-caption-input"
            />
            <p className="text-right label-mono text-[#52525B] mt-1">{caption.length} / 240</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#27272A]">
            <button
              type="submit"
              disabled={busy || !file}
              className="btn-neon flex items-center gap-2 disabled:opacity-50"
              data-testid="submit-post-btn"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {busy ? "POSTING..." : "POST TO THE WALL"}
            </button>
            <p className="label-mono text-[#71717A] text-[10px]">
              // POSTS ARE MODERATED. YOURS WILL GO LIVE AFTER ADMIN APPROVAL.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
